import { module } from 'modujs';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import gsap from 'gsap';

export default class extends module {
    constructor(m) {
        super(m);

        this.appId     = this.getData('app-id');
        this.apiKey    = this.getData('api-key');
        this.index     = this.getData('index');
        this.lang      = this.getData('lang');
        this.baseUrl   = this.getData('url');
        this.resultsId = this.getData('results-id') || null;

        this.debounceTimer = null;
        this.placeholderTl = null;

        this.events = {
            input: {
                input: 'onInput'
            },
            focusin: {
                input: 'onFocus'
            },
            focusout: {
                input: 'onBlur'
            },
            submit: {
                form: 'onSubmit'
            }
        };
    }

    init() {
        this.$input = this.$('input')[0];

        const placeholders = this.$input.dataset.searchInputPlaceholders;
        if (placeholders && !this.$input.value.trim()) {
            const list = placeholders.split('|').map(s => s.trim()).filter(Boolean);
            if (list.length > 0) {
                this._initPlaceholderAnimation(list);
            }
        }

        if (this.$input.value.trim()) {
            this.el.classList.add('has-value');
        }

        this.client = algoliasearch(this.appId, this.apiKey);

        this.onClickOutsideBind = this.onClickOutside.bind(this);
        this.onEscapeBind = this.onEscape.bind(this);

        document.addEventListener('click', this.onClickOutsideBind);
        document.addEventListener('keyup', this.onEscapeBind);
    }

    _initPlaceholderAnimation(list) {
        const wrap = document.createElement('div');
        wrap.className = 'c-search-input_fake-placeholder';
        wrap.setAttribute('aria-hidden', 'true');

        const spans = list.map(word => {
            const span = document.createElement('span');
            span.className = 'c-search-input_fake-word';
            span.textContent = word;
            wrap.appendChild(span);
            return span;
        });

        this.$('form')[0].appendChild(wrap);
        this.$fakePlaceholder = wrap;

        gsap.set(spans, { yPercent: 110 });

        this.placeholderTl = gsap.timeline({ repeat: -1, delay: 0.5, defaults: { ease: 'power2.out', duration: 0.5, } });

        spans.forEach(span => {
            this.placeholderTl
                .to(span, { yPercent: 0, })
                .to(span, { yPercent: -110, }, '+=2.5')
                .set(span, { yPercent: 110 });
        });
    }

    onFocus() {
        if (this.placeholderTl) {
            this.placeholderTl.pause();
            this.el.classList.add('is-focused');
        }

        const query = this.$input.value.trim();
        if (query) {
            this.onInput();
        }
    }

    onBlur() {
        if (this.placeholderTl && !this.$input.value.trim()) {
            this.el.classList.remove('is-focused');
            this.placeholderTl.resume();
        }
    }

    onInput() {
        clearTimeout(this.debounceTimer);

        const query = this.$input.value.trim();

        this.el.classList.toggle('has-value', !!query);

        if (!query) {
            this.callResults('clear', null);
            return;
        }

        this.debounceTimer = setTimeout(async () => {
            this.el.classList.add('is-loading');
            const { hits, nbHits } = await this.fetchResults(query);
            this.el.classList.remove('is-loading');
            this.callResults('setResults', { hits, nbHits, query, baseUrl: this.baseUrl, lang: this.lang });
        }, 300);
    }

    onSubmit(e) {
        e.preventDefault();

        const query = this.$input.value.trim();

        this.callResults('clear', null);

        if (query) {
            window.location.href = `${this.baseUrl}?s=${encodeURIComponent(query)}`;
        }
    }

    onClickOutside(e) {
        if (!this.el.contains(e.target) && !document.documentElement.classList.contains('has-search-open')) {
            this.callResults('clear', null);
        }
    }

    onEscape(e) {
        if (e.key === 'Escape') {
            this.callResults('clear', null);
        }
    }

    callResults(method, args) {
        if (this.resultsId) {
            this.call(method, args, 'SearchResults', this.resultsId);
        } else {
            this.call(method, args, 'SearchResults');
        }
    }

    async fetchResults(query) {
        try {
            const response = await this.client.search({
                requests: [{
                    indexName: this.index,
                    query,
                    hitsPerPage: 5,
                    filters: `language:${this.lang}`
                }]
            });

            const result = response.results[0];
            return { hits: result.hits || [], nbHits: result.nbHits || 0 };
        } catch {
            return { hits: [], nbHits: 0 };
        }
    }

    destroy() {
        clearTimeout(this.debounceTimer);
        if (this.placeholderTl) this.placeholderTl.kill();
        document.removeEventListener('click', this.onClickOutsideBind);
        document.removeEventListener('keyup', this.onEscapeBind);
    }
}
